"use client";

import { useEffect, useMemo, useState } from "react";
import type { CreateDraft, Project } from "@easyshot/shared";
import Link from "next/link";
import { PLATFORM_PRESETS, STYLE_OPTIONS } from "@easyshot/shared";
import { api } from "@/lib/api";
import { draftStepLabel } from "@/lib/guided-ugc";
import { Card, Spinner } from "@/components/ui";

export default function AnalyticsPage() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [drafts, setDrafts] = useState<CreateDraft[] | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .listProjects()
      .then((p) => alive && setProjects(p))
      .catch(() => alive && setProjects([]));
    api.listDrafts().then((d) => alive && setDrafts(d)).catch(() => alive && setDrafts([]));
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => {
    if (!projects) return null;
    const jobs = projects.flatMap((p) => p.jobs);
    const byStatus = count(jobs.map((j) => j.status));
    const byPlatform = count(jobs.map((j) => j.platform));
    const byStyle = count(projects.map((p) => p.style));
    return {
      projects: projects.length,
      videos: jobs.length,
      ready: byStatus.get("succeeded") ?? 0,
      failed: byStatus.get("failed") ?? 0,
      rendering:
        jobs.length -
        (byStatus.get("succeeded") ?? 0) -
        (byStatus.get("failed") ?? 0),
      byPlatform: [...byPlatform.entries()].sort((a, b) => b[1] - a[1]),
      byStyle: [...byStyle.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [projects]);

  if (!stats || !drafts) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  const tiles: [string, number][] = [
    ["Projects", stats.projects],
    ["Videos generated", stats.videos],
    ["Ready", stats.ready],
    ["Rendering", stats.rendering],
  ];

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
      <p className="mt-2 text-sm text-muted">
        Your creative output at a glance. Ad-account ROI sync is coming soon.
      </p>

      {drafts.length > 0 && (
        <section className="mt-8">
          <div className="mb-3 flex items-end justify-between">
            <div>
              <h2 className="text-lg font-semibold">Create drafts</h2>
              <p className="mt-1 text-[13px] text-muted">Continue unfinished videos from exactly where you stopped.</p>
            </div>
            <span className="text-xs text-muted">{drafts.length} saved</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {drafts.map((draft) => (
              <Link key={draft.id} href={`/studio?draft=${encodeURIComponent(draft.id)}`}>
                <Card className="h-full p-5 transition hover:border-white/25 hover:bg-white/[.04]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 font-medium truncate">{draft.name}</div>
                    <span className="shrink-0 rounded-full bg-[var(--accent)]/15 px-2 py-1 text-[10px] text-purple-300">
                      {draftStepLabel(draft.step, draft.payload)}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-muted">
                    Last saved {new Date(draft.updatedAt).toLocaleString()}
                  </div>
                  <div className="mt-4 text-sm font-medium text-purple-300">Continue Create →</div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-4">
        {tiles.map(([label, value]) => (
          <Card key={label} className="p-5">
            <div className="text-3xl font-semibold tracking-tight">{value}</div>
            <div className="mt-1 text-[12px] text-muted">{label}</div>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <BarCard
          title="Videos by platform"
          rows={stats.byPlatform.map(([k, v]) => [
            PLATFORM_PRESETS.find((p) => p.id === k)?.label ?? k,
            v,
          ])}
          total={stats.videos}
        />
        <BarCard
          title="Projects by style"
          rows={stats.byStyle.map(([k, v]) => [
            k === "infinitetalk_avatar"
              ? "InfiniteTalk Avatar Video"
              : STYLE_OPTIONS.find((s) => s.id === k)?.label ?? k,
            v,
          ])}
          total={stats.projects}
        />
      </div>

      {stats.failed > 0 && (
        <Card className="mt-4 p-5 text-sm text-muted">
          {stats.failed} failed render{stats.failed > 1 ? "s" : ""} — cash
          was refunded automatically. Retry them from each project page.
        </Card>
      )}
    </div>
  );
}

function BarCard({
  title,
  rows,
  total,
}: {
  title: string;
  rows: [string, number][];
  total: number;
}) {
  return (
    <Card className="p-5">
      <div className="mb-4 text-sm font-medium">{title}</div>
      {rows.length === 0 && (
        <div className="text-[13px] text-muted">Nothing yet.</div>
      )}
      <div className="space-y-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <div className="mb-1 flex items-center justify-between text-[12px]">
              <span>{label}</span>
              <span className="font-mono text-muted">{value}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
              <div
                className="h-full rounded-full bg-[var(--accent)]"
                style={{
                  width: `${total ? Math.max(4, (value / total) * 100) : 0}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function count<T>(items: T[]): Map<T, number> {
  const m = new Map<T, number>();
  for (const it of items) m.set(it, (m.get(it) ?? 0) + 1);
  return m;
}
